/**
 * Shared SumUp operator card-settlement workflow — the BOTH-LIVE flow (customer
 * QR + Solo reader together; first PAID leg wins), extracted from
 * SettlementsPage so it can be reused (the cash-drawer purchase widget hosts it
 * full-screen). Arms on mount, polls to resolution, cancels the intent on
 * unmount (best-effort) so abandoning the flow tears down the live legs — i.e.
 * RE-MOUNTING starts a fresh payment, like the kiosk.
 *
 * Props:
 *  - strichlisteUserId / amountCents (null = full debt) — what to charge.
 *  - onSettled()        — a settlement was created; the host refreshes its data.
 *  - onSwitchToManual?  — optional; when given, a failure offers "Switch to
 *                         Manual / cash" (the Settlements modal has a manual path;
 *                         the cash widget doesn't, so it omits this).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, QRCode, Space, Spin, Typography } from "antd";

import { ApiError, api } from "../api/client";

const { Text, Title } = Typography;

const READER_POLL_MS = 2_000;
// Stop polling after ~3 minutes (a forgotten flow shouldn't poll forever); the
// host can re-open to retry.
const READER_POLL_MAX = 90;

// Reason-specific copy for the structured orchestrator error codes (mirrors the
// kiosk's handlePrimeError mapping); unknown codes fall back to the raw detail.
const READER_ERROR_COPY: Record<string, string> = {
  reader_offline:
    "The card reader is offline — the customer can still pay by QR, or switch to Manual / cash.",
  reader_busy:
    "The card reader is busy with another payment — wait a moment and try again.",
  no_payment_channel:
    "No card-payment channel is available — check Settings → SumUp.",
  nothing_to_settle:
    "There's nothing to settle — this user's balance is already €0.",
  payout_not_allowed:
    "This user has credit, not debt — card settlement only collects debts.",
  payment_in_progress:
    "A payment is already in progress for this user — wait for it to finish or cancel it.",
  amount_out_of_bounds: "That amount is out of bounds for this balance.",
  sumup_not_configured: "SumUp is turned off — enable it in Settings → SumUp.",
  strichliste_unavailable: "Couldn't reach strichliste — try again in a moment.",
};

/** Online (QR) method id → label, for the both-live methods line. */
const READER_METHOD_LABELS: Record<string, string> = {
  card: "Card",
  apple_pay: "Apple Pay",
  google_pay: "Google Pay",
  paypal: "PayPal",
};

// The Solo's real card-present set (SumUp exposes no per-reader methods API). It
// accepts girocard/EC + contactless (never in the online list) and major cards.
const READER_ACCEPTS_LABEL = [
  "girocard / EC",
  "Visa",
  "Mastercard",
  "Contactless",
  "Apple Pay",
  "Google Pay",
].join(" · ");

/** A genuine decline only when NOTHING can still complete (mirrors the kiosk +
 * the backend _maybe_fail_intent): a dead reader leg must not abort a live QR. */
const legDeclined = (legs: { status: string }[]): boolean => {
  let anyLive = false;
  let anyDead = false;
  for (const l of legs) {
    if (l.status === "pending" || l.status === "paid") anyLive = true;
    else if (
      l.status === "declined" ||
      l.status === "failed" ||
      l.status === "cancelled" ||
      l.status === "expired"
    )
      anyDead = true;
  }
  return anyDead && !anyLive;
};

const fmtCents = (cents: number) =>
  new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(
    cents / 100,
  );

type Phase = "arming" | "waiting" | "syncing" | "done" | "failed";

interface State {
  phase: Phase;
  intentId: number | null;
  settlementId: number | null;
  message: string | null;
  hostedCheckoutUrl: string | null;
  readerArmed: boolean;
  amountCents: number | null;
  methods: string[];
}

const ARMING: State = {
  phase: "arming",
  intentId: null,
  settlementId: null,
  message: "Starting…",
  hostedCheckoutUrl: null,
  readerArmed: false,
  amountCents: null,
  methods: [],
};

export function SumupReaderSettle({
  strichlisteUserId,
  amountCents,
  onSettled,
  onSwitchToManual,
}: {
  strichlisteUserId: number;
  amountCents: number | null;
  onSettled: () => void;
  onSwitchToManual?: () => void;
}) {
  const [st, setSt] = useState<State>(ARMING);
  // Refs so the unmount-cancel + poll see live values without re-subscribing.
  const intentRef = useRef<number | null>(null);
  const settledRef = useRef(false);
  const onSettledRef = useRef(onSettled);
  onSettledRef.current = onSettled;

  const arm = useCallback(async () => {
    setSt(ARMING);
    // Tear down any prior intent from this component (a "Try again") so we mint a
    // fresh one rather than re-attaching to a stale awaiting intent.
    const prior = intentRef.current;
    intentRef.current = null;
    settledRef.current = false;
    if (prior != null) {
      try {
        await api.operatorSumupCancel(prior);
      } catch {
        /* best-effort */
      }
    }
    try {
      const result = await api.operatorReaderSettle({
        strichliste_user_id: strichlisteUserId,
        amount_cents: amountCents ?? undefined,
      });
      intentRef.current = result.intent_id;
      // Need at least one live payable channel (a QR url OR an armed reader),
      // mirroring the kiosk's canPay guard.
      const canPay = Boolean(result.hosted_checkout_url) || result.reader_armed;
      if (!canPay) {
        setSt({
          ...ARMING,
          phase: "failed",
          intentId: result.intent_id,
          message:
            "No card payment channel is available right now — switch to Manual / cash, or try again.",
        });
        return;
      }
      setSt({
        phase: "waiting",
        intentId: result.intent_id,
        settlementId: null,
        message: null,
        hostedCheckoutUrl: result.hosted_checkout_url,
        readerArmed: result.reader_armed,
        amountCents: result.amount_cents,
        methods: result.online_payment_methods,
      });
    } catch (err) {
      const code = err instanceof ApiError ? err.errorCode : null;
      const message =
        (code && READER_ERROR_COPY[code]) ||
        (err instanceof ApiError ? err.detail : String(err));
      intentRef.current = null;
      setSt({ ...ARMING, phase: "failed", message });
    }
  }, [strichlisteUserId, amountCents]);

  // Arm once on mount. Re-mounting (the host reopening) starts a fresh payment.
  useEffect(() => {
    void arm();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cancel a non-terminal intent on unmount (abandoning the flow), best-effort.
  useEffect(() => {
    return () => {
      const id = intentRef.current;
      if (id != null && !settledRef.current) {
        void api.operatorSumupCancel(id).catch(() => {});
      }
    };
  }, []);

  // Poll the primed intent to resolution. A dead reader leg never aborts a live
  // QR (legDeclined). A late online-leg URL is surfaced into the QR panel.
  useEffect(() => {
    const intentId = st.intentId;
    if (intentId == null) return;
    if (st.phase !== "waiting" && st.phase !== "syncing") return;

    let cancelled = false;
    let polls = 0;
    const tick = async () => {
      polls += 1;
      try {
        const view = await api.operatorSumupStatus(intentId);
        if (cancelled) return;
        if (view.status === "paid") {
          if (view.settlement_id != null) {
            settledRef.current = true;
            setSt((prev) => ({
              ...prev,
              phase: "done",
              settlementId: view.settlement_id,
              message: "Payment settled.",
            }));
            onSettledRef.current();
          } else {
            setSt((prev) => ({
              ...prev,
              phase: "syncing",
              message: "Card charged — finalizing the settlement…",
            }));
          }
          return;
        }
        if (view.status === "cancelled" || view.status === "expired") {
          setSt((prev) => ({
            ...prev,
            phase: "failed",
            message: `Payment ${view.status}.`,
          }));
          return;
        }
        if (view.status === "failed" || legDeclined(view.legs)) {
          setSt((prev) => ({
            ...prev,
            phase: "failed",
            message: "Payment declined — try again, or use Manual / cash.",
          }));
          return;
        }
        // awaiting_payment → keep waiting.
      } catch {
        // Transient — keep polling until the cap.
      }
      if (!cancelled && polls >= READER_POLL_MAX) {
        setSt((prev) =>
          prev.phase === "waiting" || prev.phase === "syncing"
            ? {
                ...prev,
                phase: "failed",
                message:
                  "Timed out waiting for payment. Check the SumUp transactions view; it may still be syncing.",
              }
            : prev,
        );
      }
    };
    void tick();
    const interval = window.setInterval(() => void tick(), READER_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, [st.intentId, st.phase]);

  // ---- render ----
  if (st.phase === "done") {
    return (
      <Alert
        type="success"
        showIcon
        message="Payment settled"
        description={
          <Space direction="vertical" size={2}>
            <Text>The balance is settled.</Text>
            {st.settlementId != null && (
              <a
                href={api.settlementReceiptUrl(st.settlementId)}
                target="_blank"
                rel="noreferrer"
              >
                View receipt #{st.settlementId}
              </a>
            )}
          </Space>
        }
      />
    );
  }
  if (st.phase === "failed") {
    return (
      <Alert
        type="error"
        showIcon
        message="Payment didn't complete"
        description={
          <Space direction="vertical" size={10}>
            <Text>{st.message ?? "The payment failed."}</Text>
            <Space>
              {onSwitchToManual && (
                <Button onClick={onSwitchToManual}>Switch to Manual / cash</Button>
              )}
              <Button type="primary" onClick={() => void arm()}>
                Try again
              </Button>
            </Space>
          </Space>
        }
      />
    );
  }
  if (st.phase === "arming") {
    return (
      <Alert type="info" icon={<Spin size="small" />} showIcon message="Starting…" />
    );
  }
  if (st.phase === "syncing") {
    return (
      <Alert
        type="info"
        icon={<Spin size="small" />}
        showIcon
        message="Card charged — finalizing"
        description={st.message ?? undefined}
      />
    );
  }
  // waiting — the both-live panel: amount, the customer QR, and the reader.
  const methodsLine = st.methods
    .map((m) => READER_METHOD_LABELS[m] ?? m)
    .join(" · ");
  return (
    <div style={{ marginTop: 12 }}>
      <Space
        direction="vertical"
        align="center"
        size={16}
        style={{ width: "100%" }}
      >
        {st.amountCents != null && (
          <Title level={2} style={{ margin: 0 }}>
            {fmtCents(st.amountCents)}
          </Title>
        )}
        {st.hostedCheckoutUrl && (
          <Space direction="vertical" align="center" size={6}>
            <Text strong>📱 Have the customer scan to pay</Text>
            <QRCode value={st.hostedCheckoutUrl} size={220} />
            {methodsLine && <Text type="secondary">{methodsLine}</Text>}
          </Space>
        )}
        {st.readerArmed ? (
          <Space direction="vertical" align="center" size={2}>
            <Text strong>💳 …or tap the card on the reader</Text>
            <Text type="secondary">Reader accepts: {READER_ACCEPTS_LABEL}</Text>
          </Space>
        ) : (
          <Alert
            type="warning"
            showIcon
            style={{ width: "100%" }}
            message="Card reader isn't working right now"
            description="Have the customer scan the QR above, or switch to Manual / cash."
          />
        )}
        <Space size={6}>
          <Spin size="small" />
          <Text type="secondary">Waiting for payment…</Text>
        </Space>
      </Space>
    </div>
  );
}
